// src/pages/Dashboard/Admin/Requests/hooks/useRequestsDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  createRequest,
  deleteRequest,
  getRequestsByAssociationId,
  updateRequest,
} from "../../../../../services/requestsServices";

import {
  DEFAULT_REQUEST_FILTERS,
  buildCreateRequestPayload,
  buildUpdateRequestPayload,
  calculateRequestsStats,
  filterRequests,
  safeArray,
  sortRequests,
} from "../utils/requestsDashboardUtils";

export function useRequestsDashboard() {
  const associationId = process.env.REACT_APP_ASSO_ID;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState(DEFAULT_REQUEST_FILTERS);

  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editableRequest, setEditableRequest] = useState(null);
  const [submittingForm, setSubmittingForm] = useState(false);

  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [deletingId, setDeletingId] = useState(null);

  const fetchRequests = useCallback(async () => {
    if (!associationId) {
      toast.error("Association introuvable : REACT_APP_ASSO_ID manquant.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const data = await getRequestsByAssociationId(associationId);
      setRequests(sortRequests(safeArray(data), filters.sort));
    } catch (error) {
      console.error("Erreur chargement requêtes :", error);
      toast.error("Erreur lors du chargement des requêtes.");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [associationId, filters.sort]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filteredRequests = useMemo(() => {
    return filterRequests(requests, filters);
  }, [requests, filters]);

  const stats = useMemo(() => calculateRequestsStats(requests), [requests]);

  const filteredStats = useMemo(
    () => calculateRequestsStats(filteredRequests),
    [filteredRequests]
  );

  const setFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_REQUEST_FILTERS);
  };

  const openCreateDrawer = () => {
    setFormMode("create");
    setEditableRequest(null);
    setFormDrawerOpen(true);
  };

  const openEditDrawer = (request) => {
    setFormMode("edit");
    setEditableRequest(request || null);
    setFormDrawerOpen(true);
  };

  const closeFormDrawer = () => {
    if (submittingForm) return;

    setFormDrawerOpen(false);
    setFormMode("create");
    setEditableRequest(null);
  };

  const handleCreateRequest = async (formData) => {
    try {
      setSubmittingForm(true);

      await createRequest(buildCreateRequestPayload(formData, associationId));

      toast.success("Requête envoyée avec succès.");
      await fetchRequests();
      closeFormDrawer();
    } catch (error) {
      console.error("Erreur création requête :", error);
      toast.error("Erreur lors de l’envoi de la requête.");
    } finally {
      setSubmittingForm(false);
    }
  };

  const handleUpdateRequest = async (formData) => {
    if (!editableRequest?.id) {
      toast.error("Requête introuvable.");
      return;
    }

    try {
      setSubmittingForm(true);

      await updateRequest(
        editableRequest.id,
        buildUpdateRequestPayload(formData)
      );

      toast.success("Requête mise à jour avec succès.");
      await fetchRequests();
      closeFormDrawer();
    } catch (error) {
      console.error("Erreur modification requête :", error);
      toast.error("Erreur lors de la modification de la requête.");
    } finally {
      setSubmittingForm(false);
    }
  };

  const handleDeleteRequest = async (request) => {
    if (!request?.id) return;

    if (
      !window.confirm(
        `Supprimer définitivement la requête « ${
          request.title || "sans titre"
        } » ?`
      )
    ) {
      return;
    }

    try {
      setDeletingId(request.id);

      await deleteRequest(request.id);

      toast.success("Requête supprimée avec succès.");
      setRequests((prev) => prev.filter((item) => item.id !== request.id));

      if (selectedRequest?.id === request.id) {
        setDetailsDrawerOpen(false);
        setSelectedRequest(null);
      }
    } catch (error) {
      console.error("Erreur suppression requête :", error);
      toast.error("Erreur lors de la suppression de la requête.");
    } finally {
      setDeletingId(null);
    }
  };

  const openDetailsDrawer = (request) => {
    setSelectedRequest(request || null);
    setDetailsDrawerOpen(true);
  };

  const closeDetailsDrawer = () => {
    setDetailsDrawerOpen(false);
    setSelectedRequest(null);
  };

  return {
    requests,
    filteredRequests,
    loading,

    filters,
    setFilter,
    resetFilters,

    stats,
    filteredStats,

    formDrawerOpen,
    formMode,
    editableRequest,
    submittingForm,
    openCreateDrawer,
    openEditDrawer,
    closeFormDrawer,
    handleCreateRequest,
    handleUpdateRequest,

    detailsDrawerOpen,
    selectedRequest,
    openDetailsDrawer,
    closeDetailsDrawer,

    deletingId,
    handleDeleteRequest,

    fetchRequests,
  };
}